// Assessment endpoints (M8-ASSESS; 02 §Assessment; x-ray.md §3.2, §11; 05 `assessment.*`;
// founder-approved 2026-10-22 (fix everything)). A manager (owner, admin) runs rounds of the team
// X-Ray from Setup › Structure › Assessments: creates a round with its questionnaire (`full` or
// the `delta` quick scan) and who answers it (members, and `guest` respondents by email), opens
// it (each respondent gets a magic link that expires), follows the answers and reminds (twice at
// most, 20 h apart), and closes it. Closing computes the report through `@companyos/domain-assess`
// (an immutable revision) and the proposals it makes to the org chart; accepted proposals are
// previewed as a diff and applied only on confirmation, as the org writes do (seats,
// responsibilities, metrics, with their events).
//
// A respondent answers at `/v1/companies/{co}/respond/{token}` (access `assessment_token`): the
// token is the credential and reaches that one questionnaire, nothing else. A member may also
// take a fresh link to their own questionnaire (`my-link`).
//
// Reads of answers and reports are managers' (05 `assessment.read_aggregate` and
// `assessment.read_member_detail` are both `company#manager` for now). Team-facing numbers are
// hours, never money (x-ray.md §8 rule 6).
import { z } from "zod";
import { AssessmentParticipant, AssessmentProposalRow, AssessmentRound } from "../assess-rows";
import { AssessmentQuestionnaire } from "../enums";
import { Email, LIMITS, Timestamp, text, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

// Answers -----------------------------------------------------------------------------------

const Scale = z.int().min(1).max(5);

export const AssessmentTaskAnswer = z
  .strictObject({
    name: z.string().max(LIMITS.name),
    hours_per_week: z.number().min(0).max(80).nullable(),
    energy: z.int().min(-2).max(2).nullable(),
    enjoy: z.enum(["love", "like", "meh", "dread"]).nullable(),
    skill: z.enum(["Basic", "Intermediate", "Advanced"]).nullable(),
    pillar: z.string().max(LIMITS.key).nullable(),
    someone_else: z.boolean(),
    why: z.string().max(400).nullable(),
  })
  .meta({ id: "AssessmentTaskAnswer" });

export const AssessmentAnswers = z
  .strictObject({
    role_title: z.string().max(120).nullable(),
    clarity: z
      .strictObject({
        responsibilities: Scale.optional(),
        success_definition: Scale.optional(),
        pulled_into_others: Scale.optional(),
        handoff_clarity: Scale.optional(),
      })
      .nullable(),
    tasks: z.array(AssessmentTaskAnswer).max(30),
    capacity_hours: z.number().min(0).max(120).nullable(),
    load_band: z.enum(["underloaded", "comfortable", "full", "overloaded", "drowning"]).nullable(),
    management: z
      .strictObject({
        mgmt_share: z.number().min(0).max(100).nullable(),
        team_load_read: z
          .enum(["underloaded", "comfortable", "full", "overloaded", "drowning"])
          .nullable(),
        team_clarity_read: Scale.nullable(),
        meeting_overhead_hours: z.number().min(0).max(80).nullable(),
        bottleneck: z.string().max(1000).nullable(),
      })
      .nullable(),
    friction: z
      .strictObject({
        cracks: z.string().max(LIMITS.text),
        invisible: z.string().max(LIMITS.text),
        drop: z.string().max(LIMITS.text),
      })
      .nullable(),
  })
  .meta({ id: "AssessmentAnswers" });
export type AssessmentAnswers = z.infer<typeof AssessmentAnswers>;

// Report (the body of a revision; `domain-assess` RoundReport) -------------------------------

const Health = z.enum(["green", "yellow", "red", "grey"]);
const Severity = z.enum(["high", "medium", "low"]);

export const AssessmentReportBody = z
  .object({
    scoring_version: z.string(),
    participation: z.object({
      invited: z.int().min(0),
      completed: z.int().min(0),
      rate: z.number(),
      low: z.boolean(),
      non_participants: z.array(z.object({ participant_id: z.string(), name: z.string() })),
    }),
    team: z.object({
      claimed_hours: z.number(),
      capacity_hours: z.number().nullable(),
      overloaded: z.int().min(0),
      underused: z.int().min(0),
      energy_score: z.number(),
      energizing_hours: z.number(),
      neutral_hours: z.number(),
      draining_hours: z.number(),
      aligned_share: z.number().nullable(),
    }),
    roles: z.array(
      z.object({
        participant_id: z.string(),
        name: z.string(),
        role_title: z.string().nullable(),
        participated: z.boolean(),
        claimed_hours: z.number(),
        capacity_hours: z.number().nullable(),
        load_index: z.number().nullable(),
        load_flag: z.enum(["overloaded", "underutilized", "balanced", "unknown"]),
        energy_score: z.number(),
        draining_share: z.number(),
        fit_risk: z.enum(["high", "moderate", "low", "none"]),
        misaligned_hours: z.number(),
        clarity_score: z.number().nullable(),
        health: Health,
        top_tasks: z.array(z.object({ name: z.string(), hours: z.number() })),
        handoff_hours: z.number(),
      }),
    ),
    gaps: z.array(
      z.object({
        kind: z.enum([
          "participation",
          "overload",
          "underuse",
          "crack",
          "overlap",
          "manager_gap",
          "clarity",
          "goal",
        ]),
        severity: Severity,
        title: z.string(),
        detail: z.string(),
        people: z.array(z.string()),
      }),
    ),
    action_plan: z.object({
      hours_recovered: z.number(),
      workdays_recovered: z.number(),
      tasks_handed_off: z.int().min(0),
    }),
    hire_signal: z.object({
      suggested_role: z.string().nullable(),
      hours_per_week: z.number(),
      by_pillar: z.array(
        z.object({
          pillar: z.string(),
          hours_per_week: z.number(),
          task_count: z.int().min(0),
          top_tasks: z.array(z.string()),
        }),
      ),
    }),
    actions: z.array(
      z.object({
        key: z.string(),
        kind: z.enum([
          "hire",
          "rebalance",
          "clarify",
          "fix_crack",
          "dedupe",
          "drop",
          "participation",
        ]),
        title: z.string(),
        detail: z.string(),
        hours_per_week: z.number().nullable(),
      }),
    ),
  })
  .meta({ id: "AssessmentReportBody" });
export type AssessmentReportBody = z.infer<typeof AssessmentReportBody>;

// Rounds ------------------------------------------------------------------------------------

export const AssessmentQuestionnaireInfo = z
  .object({
    key: AssessmentQuestionnaire,
    label: z.string(),
    description: z.string(),
    minutes: z.int().min(1),
  })
  .meta({ id: "AssessmentQuestionnaireInfo" });

export const AssessmentProgress = z
  .object({
    invited: z.int().min(0),
    opened: z.int().min(0),
    in_progress: z.int().min(0),
    completed: z.int().min(0),
    expired: z.int().min(0),
  })
  .meta({ id: "AssessmentProgress" });

export const AssessmentRoundSummary = AssessmentRound.extend({
  progress: AssessmentProgress,
  respondents: z.int().min(0),
  report_version: z.int().min(1).nullable(),
}).meta({ id: "AssessmentRoundSummary" });
export type AssessmentRoundSummary = z.infer<typeof AssessmentRoundSummary>;

export const AssessmentRoundList = z
  .object({
    data: z.array(AssessmentRoundSummary),
    questionnaires: z.array(AssessmentQuestionnaireInfo),
  })
  .meta({ id: "AssessmentRoundList" });

/**
 * A respondent as the round's progress table shows it: the row (no token), how far the draft
 * got (0..1), the seat's title, and whether a reminder may go now (x-ray.md §2.4
 * `isReminderEligible`: two at most, 20 h apart, not after answering).
 */
export const AssessmentParticipantView = AssessmentParticipant.extend({
  answered_share: z.number().min(0).max(1),
  seat_title: z.string().nullable(),
  can_remind: z.boolean(),
}).meta({ id: "AssessmentParticipantView" });
export type AssessmentParticipantView = z.infer<typeof AssessmentParticipantView>;

/** A magic link minted for a respondent: shown once to the manager (no email is sent). */
export const AssessmentLink = z
  .object({
    participant_id: Uuid,
    display_name: z.string(),
    /** The web path: `/respond/{co}/{token}`. */
    path: z.string(),
    expires_at: Timestamp,
  })
  .meta({ id: "AssessmentLink" });

export const AssessmentRoundDetail = AssessmentRoundSummary.extend({
  participants: z.array(AssessmentParticipantView),
  questionnaire_info: AssessmentQuestionnaireInfo,
  /** Links minted by this call (open, adding people to an open round); empty otherwise. */
  links: z.array(AssessmentLink),
}).meta({ id: "AssessmentRoundDetail" });
export type AssessmentRoundDetail = z.infer<typeof AssessmentRoundDetail>;

const Name = text(LIMITS.name);

export const AssessmentGuestInput = z
  .strictObject({
    email: Email,
    name: Name,
    role_title: z.string().trim().max(120).nullable().optional(),
    /** The seat they report to (for the chart and the manager cross-check). */
    reports_to_seat_id: Uuid.nullable().optional(),
  })
  .meta({ id: "AssessmentGuestInput" });

export const AssessmentPeopleInput = z
  .strictObject({
    /** Active person memberships of the company. */
    member_ids: z.array(Uuid).max(LIMITS.list).optional(),
    guests: z.array(AssessmentGuestInput).max(LIMITS.list).optional(),
  })
  .meta({ id: "AssessmentPeopleInput" });

export const AssessmentRoundCreate = z
  .strictObject({
    /** Default: "Team X-Ray {n}" or "Quick scan {n}". */
    name: Name.optional(),
    questionnaire: AssessmentQuestionnaire,
    invite_expiry_days: z.int().min(1).max(60).optional(),
    member_ids: z.array(Uuid).max(LIMITS.list).optional(),
    guests: z.array(AssessmentGuestInput).max(LIMITS.list).optional(),
    /** Open it at once (mint the links). Default false: a draft. */
    open: z.boolean().optional(),
  })
  .meta({ id: "AssessmentRoundCreate" });

export const AssessmentRoundPatch = z
  .strictObject({
    name: Name.optional(),
    invite_expiry_days: z.int().min(1).max(60).optional(),
  })
  .meta({ id: "AssessmentRoundPatch" });

export const AssessmentParticipantParams = z.object({
  co: Uuid,
  id: Uuid,
  participant_id: Uuid,
});

export const AssessmentReminder = z
  .object({ participant: AssessmentParticipantView, link: AssessmentLink })
  .meta({ id: "AssessmentReminder" });

// Report and proposals ----------------------------------------------------------------------

export const AssessmentProposalView = AssessmentProposalRow.extend({
  /** The proposed seat it hangs on is not accepted (accepting this needs it). */
  blocked: z.boolean(),
  /** The existing seat it names, by title (null: on a proposed seat or the chart's top). */
  seat_title: z.string().nullable(),
}).meta({ id: "AssessmentProposalView" });
export type AssessmentProposalView = z.infer<typeof AssessmentProposalView>;

export const AssessmentReportView = z
  .object({
    round: AssessmentRound,
    report_id: Uuid,
    version: z.int().min(1),
    scoring_version: z.string(),
    generated_at: Timestamp,
    body: AssessmentReportBody,
    proposals: z.array(AssessmentProposalView),
  })
  .meta({ id: "AssessmentReportView" });
export type AssessmentReportView = z.infer<typeof AssessmentReportView>;

export const AssessmentProposalDecide = z
  .strictObject({ state: z.enum(["proposed", "accepted", "rejected"]) })
  .meta({ id: "AssessmentProposalDecide" });

export const AssessmentApplySelection = z
  .strictObject({
    /** Accepted proposals to apply; default: every accepted one not applied yet. */
    proposal_ids: z.array(Uuid).max(LIMITS.list).optional(),
  })
  .meta({ id: "AssessmentApplySelection" });

/** One line of the diff: what applying would write, reuse or skip, and why. */
export const AssessmentDiffLine = z
  .object({
    proposal_id: Uuid,
    kind: z.enum(["role", "seat", "responsibility", "metric", "seat_metric"]),
    action: z.enum(["create", "reuse", "skip"]),
    name: z.string(),
    /** Where it lands: the seat (existing or proposed) it is on, or the seat it reports to. */
    parent: z.string().nullable(),
    reason: z.string().nullable(),
  })
  .meta({ id: "AssessmentDiffLine" });

export const AssessmentApplyPreview = z
  .object({
    lines: z.array(AssessmentDiffLine),
    counts: z.object({
      create: z.int().min(0),
      reuse: z.int().min(0),
      skip: z.int().min(0),
    }),
  })
  .meta({ id: "AssessmentApplyPreview" });

export const AssessmentApplyResult = z
  .object({
    applied: z.array(AssessmentProposalView),
    created: z.object({
      roles: z.array(Uuid),
      seats: z.array(Uuid),
      responsibilities: z.array(Uuid),
      metrics: z.array(Uuid),
    }),
    lines: z.array(AssessmentDiffLine),
  })
  .meta({ id: "AssessmentApplyResult" });

// A member's own questionnaires ---------------------------------------------------------------

export const AssessmentMine = z
  .object({
    data: z.array(
      z.object({
        round_id: Uuid,
        round_name: z.string(),
        questionnaire: AssessmentQuestionnaire,
        participant_id: Uuid,
        state: AssessmentParticipant.shape.state,
        expires_at: Timestamp.nullable(),
        minutes: z.int().min(1),
      }),
    ),
  })
  .meta({ id: "AssessmentMine" });

export const AssessmentMyLink = z
  .object({ path: z.string(), expires_at: Timestamp })
  .meta({ id: "AssessmentMyLink" });

// The respondent's page ---------------------------------------------------------------------

export const AssessmentTokenParams = z.object({
  co: Uuid,
  token: z.string().min(20).max(200),
});

export const AssessmentQuestionnaireView = z
  .object({
    company_name: z.string(),
    round_name: z.string(),
    questionnaire: AssessmentQuestionnaireInfo,
    steps: z.array(z.enum(["role", "tasks", "ratings", "capacity", "management", "friction"])),
    choices: z.object({
      energy: z.array(z.object({ value: z.int(), label: z.string() })),
      enjoy: z.array(z.object({ value: z.string(), label: z.string() })),
      skill: z.array(z.object({ value: z.string(), label: z.string() })),
      pillar: z.array(z.object({ value: z.string(), label: z.string() })),
      load_band: z.array(z.object({ value: z.string(), label: z.string(), hint: z.string() })),
      clarity: z.array(z.object({ key: z.string(), label: z.string() })),
      friction: z.array(z.object({ key: z.string(), label: z.string() })),
    }),
    respondent: z.object({
      name: z.string(),
      kind: AssessmentParticipant.shape.kind,
      role_title: z.string().nullable(),
      is_manager: z.boolean(),
    }),
    /** Who sees the answers (the consent line, x-ray.md §2.4 `visibilitySummary`). */
    visibility: z.string(),
    answers: AssessmentAnswers,
    answered_share: z.number().min(0).max(1),
    state: AssessmentParticipant.shape.state,
    submitted_at: Timestamp.nullable(),
    expires_at: Timestamp.nullable(),
    /** Field errors of the last submit attempt (empty when none). */
    errors: z.array(z.object({ path: z.string(), message: z.string() })),
  })
  .meta({ id: "AssessmentQuestionnaireView" });
export type AssessmentQuestionnaireView = z.infer<typeof AssessmentQuestionnaireView>;

export const AssessmentAnswersSave = z
  .strictObject({ answers: AssessmentAnswers })
  .meta({ id: "AssessmentAnswersSave" });

// Routes ------------------------------------------------------------------------------------

const rounds = `${base}/assessment-rounds`;
const round = `${rounds}/{id}`;
const participant = `${round}/participants/{participant_id}`;
const respond = `${base}/respond/{token}`;

export const assessRoutes = {
  "assessments.listRounds": defineRoute({
    ...core,
    id: "assessments.listRounds",
    method: "get",
    path: rounds,
    tag: "assessments",
    summary: "The company's assessment rounds with their progress, and the questionnaires",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: AssessmentRoundList },
    errors: [],
    emits: [],
  }),
  "assessments.createRound": defineRoute({
    ...core,
    id: "assessments.createRound",
    method: "post",
    path: rounds,
    tag: "assessments",
    summary: "Create a round: its questionnaire and who answers it (opened at once on request)",
    access: "manager",
    params: CompanyParams,
    body: AssessmentRoundCreate,
    idempotency: "optional",
    response: { status: 201, schema: AssessmentRoundDetail },
    errors: ["validation_failed"],
    emits: [
      "assessment.round_created",
      "assessment.participant_invited",
      "assessment.round_opened",
    ],
  }),
  "assessments.getRound": defineRoute({
    ...core,
    id: "assessments.getRound",
    method: "get",
    path: round,
    tag: "assessments",
    summary: "A round, its respondents and how far each got",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: AssessmentRoundDetail },
    errors: [],
    emits: [],
  }),
  "assessments.updateRound": defineRoute({
    ...core,
    id: "assessments.updateRound",
    method: "patch",
    path: round,
    tag: "assessments",
    summary: "Rename a round or change how long its links last (draft or open)",
    access: "manager",
    params: CompanyItemParams,
    body: AssessmentRoundPatch,
    response: { status: 200, schema: AssessmentRoundDetail },
    errors: ["validation_failed", "assessment_state_conflict"],
    emits: ["assessment.round_updated"],
  }),
  "assessments.deleteRound": defineRoute({
    ...core,
    id: "assessments.deleteRound",
    method: "delete",
    path: round,
    tag: "assessments",
    summary: "Delete a draft round",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: ["assessment_state_conflict"],
    emits: ["assessment.round_deleted"],
  }),
  "assessments.addParticipants": defineRoute({
    ...core,
    id: "assessments.addParticipants",
    method: "post",
    path: `${round}/participants`,
    tag: "assessments",
    summary: "Ask more people (members or guests); in an open round their links are minted",
    access: "manager",
    params: CompanyItemParams,
    body: AssessmentPeopleInput,
    response: { status: 200, schema: AssessmentRoundDetail },
    errors: ["validation_failed", "assessment_state_conflict"],
    emits: ["assessment.participant_invited"],
  }),
  "assessments.removeParticipant": defineRoute({
    ...core,
    id: "assessments.removeParticipant",
    method: "delete",
    path: participant,
    tag: "assessments",
    summary: "Stop asking someone who has not answered (their link stops working)",
    access: "manager",
    params: AssessmentParticipantParams,
    response: { status: 204 },
    errors: ["assessment_state_conflict"],
    emits: ["assessment.participant_removed"],
  }),
  "assessments.openRound": defineRoute({
    ...core,
    id: "assessments.openRound",
    method: "post",
    path: `${round}/open`,
    tag: "assessments",
    summary: "Open a draft round: mint each respondent's magic link (shown once)",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: AssessmentRoundDetail },
    errors: ["assessment_state_conflict"],
    emits: ["assessment.round_opened"],
  }),
  "assessments.remind": defineRoute({
    ...core,
    id: "assessments.remind",
    method: "post",
    path: `${participant}/remind`,
    tag: "assessments",
    summary: "Remind a respondent: a fresh link (the old one stops working), twice at most",
    access: "manager",
    params: AssessmentParticipantParams,
    response: { status: 200, schema: AssessmentReminder },
    errors: ["assessment_state_conflict"],
    emits: ["assessment.participant_reminded"],
  }),
  "assessments.closeRound": defineRoute({
    ...core,
    id: "assessments.closeRound",
    method: "post",
    path: `${round}/close`,
    tag: "assessments",
    summary: "Close an open round and compute its report and org-chart proposals",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: AssessmentRoundDetail },
    errors: ["assessment_state_conflict"],
    emits: ["assessment.round_closed", "assessment.report_generated"],
  }),
  "assessments.getReport": defineRoute({
    ...core,
    id: "assessments.getReport",
    method: "get",
    path: `${round}/report`,
    tag: "assessments",
    summary: "The round's latest report revision and its proposals",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: AssessmentReportView },
    errors: [],
    emits: [],
  }),
  "assessments.reportHtml": defineRoute({
    ...core,
    id: "assessments.reportHtml",
    method: "get",
    path: `${round}/report/html`,
    tag: "assessments",
    summary: "The report as a standalone HTML page (export; prints to PDF)",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, contentType: "text/html" },
    errors: [],
    emits: [],
  }),
  "assessments.decideProposal": defineRoute({
    ...core,
    id: "assessments.decideProposal",
    method: "patch",
    path: `${base}/assessment-proposals/{id}`,
    tag: "assessments",
    summary: "Accept or reject a proposal (or set it back to proposed)",
    access: "manager",
    params: CompanyItemParams,
    body: AssessmentProposalDecide,
    response: { status: 200, schema: AssessmentProposalView },
    errors: ["validation_failed", "assessment_state_conflict"],
    emits: ["assessment.proposal_decided"],
  }),
  "assessments.previewApply": defineRoute({
    ...core,
    id: "assessments.previewApply",
    method: "post",
    path: `${round}/proposals/preview`,
    tag: "assessments",
    summary: "What applying the accepted proposals would create, reuse or skip (writes nothing)",
    access: "manager",
    params: CompanyItemParams,
    body: AssessmentApplySelection,
    safe: true,
    response: { status: 200, schema: AssessmentApplyPreview },
    errors: ["validation_failed"],
    emits: [],
  }),
  "assessments.apply": defineRoute({
    ...core,
    id: "assessments.apply",
    method: "post",
    path: `${round}/proposals/apply`,
    tag: "assessments",
    summary: "Apply the accepted proposals: seats, responsibilities and KPIs on the chart",
    access: "manager",
    params: CompanyItemParams,
    body: AssessmentApplySelection,
    idempotency: "optional",
    response: { status: 200, schema: AssessmentApplyResult },
    errors: ["validation_failed", "assessment_state_conflict", "conflict"],
    emits: [
      "assessment.proposal_applied",
      "role.created",
      "seat.created",
      "responsibility.created",
      "metric_def.created",
      "seat_metric.changed",
    ],
  }),
  "assessments.mine": defineRoute({
    ...core,
    id: "assessments.mine",
    method: "get",
    path: `${base}/assessments/mine`,
    tag: "assessments",
    summary: "The caller's own questionnaires in open rounds",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: AssessmentMine },
    errors: [],
    emits: [],
  }),
  "assessments.myLink": defineRoute({
    ...core,
    id: "assessments.myLink",
    method: "post",
    path: `${round}/my-link`,
    tag: "assessments",
    summary: "A fresh link to the caller's own questionnaire in an open round",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: AssessmentMyLink },
    errors: ["assessment_state_conflict"],
    emits: ["assessment.link_issued"],
  }),
  "respond.get": defineRoute({
    ...core,
    id: "respond.get",
    method: "get",
    path: respond,
    tag: "assessments",
    summary: "A respondent's questionnaire, by its magic link",
    access: "assessment_token",
    params: AssessmentTokenParams,
    response: { status: 200, schema: AssessmentQuestionnaireView },
    errors: ["assessment_link_expired"],
    emits: [],
  }),
  "respond.save": defineRoute({
    ...core,
    id: "respond.save",
    method: "put",
    path: respond,
    tag: "assessments",
    summary: "Save a respondent's draft answers",
    access: "assessment_token",
    params: AssessmentTokenParams,
    body: AssessmentAnswersSave,
    response: { status: 200, schema: AssessmentQuestionnaireView },
    errors: ["validation_failed", "assessment_link_expired", "assessment_state_conflict"],
    emits: ["assessment.response_saved"],
  }),
  "respond.submit": defineRoute({
    ...core,
    id: "respond.submit",
    method: "post",
    path: `${respond}/submit`,
    tag: "assessments",
    summary: "Submit the answers (validated; frozen once submitted)",
    access: "assessment_token",
    params: AssessmentTokenParams,
    body: AssessmentAnswersSave,
    response: { status: 200, schema: AssessmentQuestionnaireView },
    errors: ["validation_failed", "assessment_link_expired", "assessment_state_conflict"],
    emits: ["assessment.response_submitted"],
  }),
};
